/**
 * Bottom-to-top order of the stacked expenditure chart.
 *
 * Two rules sit on top of "biggest band first":
 *
 *  - A fixed base. The two Subventionen bands are pinned to the foot of the
 *    stack, together and in that order, with Öffentliche Leistungen directly
 *    above them. Size still orders everything above. Pinning the base is what
 *    makes the subsidy share readable off the axis instead of floating at a
 *    different height in every chart and every year.
 *  - Haushaltstechnik & Sonderposten always comes last, i.e. renders on top.
 *    It is the dashed outline band and can flip sign, so it sits above the
 *    opaque fills rather than between them.
 */

import { HAUSHALTSTECHNIK_TYPE } from './haushaltstechnik';

export const STACK_BASE_ORDER = [
  'Subventionen an Haushalte',
  'Subventionen an Unternehmen',
  'Öffentliche Leistungen',
] as const;

/** Position in STACK_BASE_ORDER, or one past the end for everything else. */
function baseRank(type: string): number {
  const index = (STACK_BASE_ORDER as readonly string[]).indexOf(type);
  return index === -1 ? STACK_BASE_ORDER.length : index;
}

/**
 * Orders expenditure types for stacking, bottom first.
 *
 * `latestValue` supplies the size a type is ranked by — the value in the
 * latest year on display, Ist or Soll as that year has it.
 */
export function orderStackTypes(
  types: readonly string[],
  latestValue: (type: string) => number,
): string[] {
  const ordered = [...types].sort((a, b) => {
    const rankA = baseRank(a);
    const rankB = baseRank(b);
    if (rankA !== rankB) return rankA - rankB;
    return latestValue(b) - latestValue(a);
  });

  const withoutHaushaltstechnik = ordered.filter(t => t !== HAUSHALTSTECHNIK_TYPE);
  return withoutHaushaltstechnik.length === ordered.length
    ? ordered
    : [...withoutHaushaltstechnik, HAUSHALTSTECHNIK_TYPE];
}
